import ExportFiscalMensuel from '../components/shared/ExportFiscalMensuel';
import { SelectCategorieDepense } from '../components/shared/SelectCategorieDepense';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Printer, Download, Trash2, Check, AlertTriangle, Sparkles } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchDepenses,
  creerDepense,
  supprimerDepense,
  reglerDepense,
  marquerDecaisse,
  fetchEcheancier,
  fetchDeclarationTEJ,
  fetchDetteFiscaleRS,
  fetchFournisseursGeneraux,
  fetchFinances,
  TAUX_TVA,
  TAUX_RS,
  MODES_REGLEMENT,
  suggererTraites,
  fetchAvances,
  verserAvance,
  soldeAvance,
  TIMBRE_FISCAL,
} from '../lib/dataProvider';
import { useAuth } from '../lib/auth';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { ContactCombobox } from '../components/shared/ContactCombobox';
import { CertificatRS } from '../components/finances/CertificatRS';
import { LotFactures } from '../components/finances/LotFactures';
import { RecuAvance } from '../components/finances/RecuAvance';
import { DepensesCanoniques, FournisseursCanoniques, RetenuesCanoniques } from '../components/canonique/BlocsCanoniques';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const td = 'px-3 py-2 text-sm text-slate-800';

const ONGLETS = [
  { id: 'factures', label: 'Factures de frais', hint: 'Saisie, TVA et retenue à la source' },
  { id: 'echeancier', label: 'Échéancier de trésorerie', hint: 'Chèques et traites à décaisser' },
  { id: 'tej', label: 'Déclaration TEJ', hint: 'Récapitulatif mensuel des retenues' },
  { id: 'canonique', label: 'Source canonique (passerelle)', hint: 'Dépenses, fournisseurs et RS du serveur' },
];

const COULEUR_STATUT = {
  'À payer': 'bg-amber-50 text-amber-800 border-amber-300',
  'Partiellement réglée': 'bg-sky-50 text-sky-800 border-sky-300',
  Réglée: 'bg-emerald-50 text-emerald-800 border-emerald-300',
};

export default function FraisGeneraux() {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  const [onglet, setOnglet] = useState('factures');
  const [depenses, setDepenses] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [echeancier, setEcheancier] = useState([]);
  const [tej, setTej] = useState([]);
  const [detteRS, setDetteRS] = useState(0);
  const [certificat, setCertificat] = useState(null);
  const [reglement, setReglement] = useState(null);
  const [copilote, setCopilote] = useState(null);
  const [avances, setAvances] = useState([]);
  const [avance, setAvance] = useState(null);
  const [recuAvance, setRecuAvance] = useState(null);
  const [f, setF] = useState({
    date: aujourdhui,
    numeroFacture: '',
    tiersId: '',
    categorie: '',
    libelle: '',
    montantHT: '',
    autresTaxes: '',
    tauxTVA: 19,
    timbreFiscal: TIMBRE_FISCAL,
    appliquerRS: false,
    tauxRS: 3,
  });

  const load = useCallback(async () => {
    setDepenses(await fetchDepenses());
    setFournisseurs(await fetchFournisseursGeneraux());
    setComptes((await fetchFinances()).soldes);
    setEcheancier(await fetchEcheancier());
    setTej(await fetchDeclarationTEJ());
    setDetteRS(await fetchDetteFiscaleRS());
    setAvances(await fetchAvances());
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const calcul = useMemo(() => {
    const ht = Number(f.montantHT) || 0;
    const taxes = Number(f.autresTaxes) || 0;
    const timbre = Number(f.timbreFiscal) || 0;
    const base = Number((ht + taxes).toFixed(3));
    const tva = Number(((base * Number(f.tauxTVA)) / 100).toFixed(3));
    const ttc = Number((base + tva + timbre).toFixed(3));
    const rs = f.appliquerRS ? Number(((ttc * Number(f.tauxRS)) / 100).toFixed(3)) : 0;
    return { ht, taxes, timbre, base, tva, ttc, rs, net: Number((ttc - rs).toFixed(3)) };
  }, [f]);

  const submit = async (e) => {
    e.preventDefault();
    const { error, depense } = await creerDepense({
      ...f,
      tauxRS: f.appliquerRS ? Number(f.tauxRS) : 0,
      tauxTVA: Number(f.tauxTVA),
      saisiPar: user?.name,
    });
    if (error) return toast.error(error);
    toast.success(`Facture ${depense.numeroFacture} enregistrée — net à payer ${fmt(depense.netAPayer, 3)} TND`);
    setF({ ...f, numeroFacture: '', montantHT: '', autresTaxes: '', libelle: '', timbreFiscal: TIMBRE_FISCAL });
    load();
    if (depense.montantRS > 0) setCertificat(depense);
  };

  const supprimer = async (d) => {
    if (!window.confirm(`Supprimer la facture ${d.numeroFacture} de ${d.fournisseur} ?`)) return;
    const { error } = await supprimerDepense(d.id, user?.name);
    if (error) return toast.error(error);
    toast.success('Facture supprimée');
    load();
  };

  const decaisser = async (l) => {
    const { error } = await marquerDecaisse(l.depenseId, l.id, user?.name);
    if (error) return toast.error(error);
    toast.success(`${l.mode} ${l.piece} décaissé — ${fmt(l.montant, 3)} TND débités`);
    load();
  };

  const exportTEJ = (m) => {
    const head = [
      'Matricule fiscal fournisseur',
      'Raison sociale',
      'Date de paiement',
      'Nature de la retenue',
      'Montant brut (base)',
      'Taux appliqué (%)',
      'Montant retenue',
      'Montant net',
    ];
    const body = m.lignes.map((l) =>
      [
        l.mf || l.cin || '',
        l.fournisseur,
        l.datePaiement ?? l.date,
        `${l.categorie}${l.libelle ? ` — ${l.libelle}` : ''}`,
        l.montantTTC,
        String(l.tauxRS).replace('.', ','),
        l.montantRS,
        l.netAPayer,
      ].join(';')
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${[head.join(';'), ...body].join('\n')}`], { type: 'text/csv;charset=utf-8;' }));
    a.download = `declaration_tej_${m.mois}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Export TEJ ${m.mois} — ${m.lignes.length} retenue(s)`);
  };

  const aPayer = depenses.filter((d) => d.statut !== 'Réglée');
  const totalNonEchu = echeancier.filter((l) => !l.echu).reduce((s, l) => s + l.montant, 0);
  const enRetard = echeancier.filter((l) => l.echu);

  return (
    <div data-testid="frais-generaux-page" className="space-y-6">
      <PageTitle
        emoji="📑"
        titre="Dépenses & Frais Généraux"
        sous="Factures officielles des fournisseurs généraux — TVA, retenue à la source (TEJ) et échéancier de trésorerie"
      />

      <ExportFiscalMensuel type="rs" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatBlock testid="stat-factures" titre="Factures enregistrées" accent="border-l-slate-900" items={[['Lignes', fmt(depenses.length)], ['TTC (TND)', fmt(depenses.reduce((s, d) => s + d.montantTTC, 0), 3)]]} />
        <StatBlock testid="stat-a-payer" titre="Reste à payer" accent="border-l-amber-500" items={[['Factures', fmt(aPayer.length)], ['Montant (TND)', fmt(aPayer.reduce((s, d) => s + d.reste, 0), 3)]]} />
        <StatBlock testid="stat-dette-fiscale" titre="Dettes fiscales — retenue à la source" accent="border-l-red-600" items={[['À reverser (TND)', fmt(detteRS, 3)]]} />
        <StatBlock testid="stat-echeancier" titre="Échéances à venir" accent="border-l-sky-600" items={[['Effets', fmt(echeancier.filter((l) => !l.echu).length)], ['Montant (TND)', fmt(totalNonEchu, 3)]]} />
      </div>

      <div data-testid="onglets-frais" className="flex flex-wrap gap-px bg-slate-200">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            type="button"
            data-testid={`onglet-${o.id}`}
            onClick={() => setOnglet(o.id)}
            className={`flex-1 px-4 py-2.5 text-left transition-colors duration-150 ${onglet === o.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            <span className="block text-sm font-bold">{o.label}</span>
            <span className={`text-xs ${onglet === o.id ? 'text-slate-300' : 'text-slate-500'}`}>{o.hint}</span>
          </button>
        ))}
      </div>

      {onglet === 'factures' && (
        <>
          <LotFactures avances={avances} onDone={load} />

          <section data-testid="avances-section" className="border border-slate-200 border-l-4 border-l-sky-600 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div>
                <p className="font-heading text-base font-bold text-slate-900">Avances / acomptes fournisseurs</p>
                <p className="text-xs text-slate-500">
                  Décaissement avant facture : le solde créditeur est imputé automatiquement à la validation de la facture.
                </p>
              </div>
              <button
                type="button"
                data-testid="verser-avance"
                onClick={() => setAvance({ tiersId: '', date: aujourdhui, montant: '', mode: 'Virement', piece: '', compteId: '' })}
                className="h-11 border-2 border-sky-700 bg-sky-700 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white hover:bg-sky-800"
              >
                Verser une avance
              </button>
            </div>
            {avances.length > 0 && (
              <table className="w-full border-collapse text-left" data-testid="table-avances">
                <thead className="bg-slate-50">
                  <tr>{['Réf.', 'Date', 'Fournisseur', 'Mode', 'N° pièce', 'Montant versé', 'Imputé', 'Solde créditeur', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {avances.map((a) => (
                    <tr key={a.id} data-testid={`avance-${a.id}`}>
                      <td className={`${td} font-data text-xs font-semibold`}>{a.numero}</td>
                      <td className={`${td} font-data text-xs`}>{a.date}</td>
                      <td className={td}>{a.fournisseur}</td>
                      <td className={`${td} text-xs`}>{a.mode}</td>
                      <td className={`${td} font-data text-xs`}>{a.piece || '—'}</td>
                      <td className={`${td} tabular font-data text-right`}>{fmt(a.montant, 3)}</td>
                      <td className={`${td} tabular font-data text-right text-xs`}>{fmt(a.impute, 3)}</td>
                      <td data-testid={`solde-avance-${a.id}`} className={`${td} tabular font-data text-right font-bold text-sky-800`}>{fmt(a.reste, 3)}</td>
                      <td className={`${td} text-right`}>
                        <button
                          type="button"
                          data-testid={`imprimer-avance-${a.id}`}
                          title="Réimprimer la décharge"
                          onClick={() => setRecuAvance({ ...a, compteNom: comptes.find((c) => c.id === a.compteId)?.nom ?? '' })}
                          className="border border-slate-300 p-2 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          <Printer size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <form onSubmit={submit} data-testid="form-frais" className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">
              Nouvelle facture de frais généraux
            </p>
            <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <label className={label}>Date de la facture</label>
                <input data-testid="frais-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Numéro de facture *</label>
                <input data-testid="frais-numero" value={f.numeroFacture} onChange={(e) => setF({ ...f, numeroFacture: e.target.value })} className={inputCls} placeholder="ex. F-2026/0148" />
              </div>
              <div>
                <label className={label}>Fournisseur général *</label>
                <ContactCombobox contacts={fournisseurs} value={f.tiersId} onChange={(id) => setF({ ...f, tiersId: id })} testid="frais-fournisseur" placeholder="Rechercher un fournisseur général…" />
                {fournisseurs.length === 0 && (
                  <p className="mt-1.5 text-[11px] text-amber-700">
                    Aucun fournisseur général : classez un contact en « Fournisseur Général » depuis Contacts.
                  </p>
                )}
              </div>
              <div>
                <label className={label}>Catégorie analytique *</label>
                <SelectCategorieDepense usage="frais-generaux" data-testid="frais-categorie" value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })} className={inputCls}>
                <option value="">— Choisir —</option>
              </SelectCategorieDepense>
              </div>

              <div>
                <label className={label}>1. Montant HT (TND) *</label>
                <input data-testid="frais-ht" type="number" step="0.001" min="0" value={f.montantHT} onChange={(e) => setF({ ...f, montantHT: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>2. Autres taxes (FODEC 1 %…)</label>
                <input data-testid="frais-autres-taxes" type="number" step="0.001" min="0" value={f.autresTaxes} onChange={(e) => setF({ ...f, autresTaxes: e.target.value })} className={inputCls} placeholder="0,000" />
              </div>
              <div>
                <label className={label}>3. Base soumise à TVA (calculée)</label>
                <p data-testid="frais-base-tva" className="tabular mt-1.5 flex h-11 items-center border border-slate-200 bg-slate-100 px-3 font-data text-sm font-semibold text-slate-900">
                  {fmt(calcul.base, 3)}
                </p>
              </div>
              <div>
                <label className={label}>4. Taux de TVA</label>
                <select data-testid="frais-tva" value={f.tauxTVA} onChange={(e) => setF({ ...f, tauxTVA: Number(e.target.value) })} className={inputCls}>
                  {TAUX_TVA.map((t) => (
                    <option key={t} value={t}>{`${t} %`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>5. Montant TVA (calculé)</label>
                <p data-testid="frais-montant-tva" className="tabular mt-1.5 flex h-11 items-center border border-slate-200 bg-slate-100 px-3 font-data text-sm font-semibold text-slate-900">
                  {fmt(calcul.tva, 3)}
                </p>
              </div>
              <div>
                <label className={label}>6. Timbre fiscal (TND)</label>
                <input data-testid="frais-timbre" type="number" step="0.001" min="0" value={f.timbreFiscal} onChange={(e) => setF({ ...f, timbreFiscal: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>7. Montant TTC de la facture</label>
                <p data-testid="frais-montant-ttc" className="tabular mt-1.5 flex h-11 items-center border-2 border-slate-900 bg-slate-50 px-3 font-data text-sm font-bold text-slate-900">
                  {fmt(calcul.ttc, 3)}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className={label}>Libellé / détail (facultatif)</label>
                <input data-testid="frais-libelle" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })} className={inputCls} placeholder="ex. Révision presse à grignons" />
              </div>

              <div className="md:col-span-2 border-l-4 border-red-600 bg-red-50/50 px-3 py-2">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <input data-testid="frais-appliquer-rs" type="checkbox" checked={f.appliquerRS} onChange={(e) => setF({ ...f, appliquerRS: e.target.checked })} className="h-4 w-4 accent-red-700" />
                  Appliquer une retenue à la source (8)
                </label>
                {f.appliquerRS && (
                  <div className="mt-2 flex flex-wrap items-end gap-3">
                    <div>
                      <label className={label}>Taux de RS</label>
                      <select data-testid="frais-taux-rs" value={f.tauxRS} onChange={(e) => setF({ ...f, tauxRS: Number(e.target.value) })} className={`${inputCls} w-32`}>
                        {TAUX_RS.map((t) => (
                          <option key={t} value={t}>{`${String(t).replace('.', ',')} %`}</option>
                        ))}
                      </select>
                    </div>
                    <p data-testid="frais-montant-rs" className="font-data text-[11px] text-red-800">
                      Retenue opérée : <span className="text-sm font-bold">{fmt(calcul.rs, 3)} TND</span> sur base TTC —
                      isolée au compte « Dettes fiscales — Retenue à la source ». Certificat généré à l'enregistrement.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p data-testid="frais-net" className="font-data text-sm text-slate-700">
                9. Net à payer au fournisseur : <span className="text-lg font-bold text-slate-900">{fmt(calcul.net, 3)} TND</span>
                {calcul.rs > 0 ? ` (TTC ${fmt(calcul.ttc, 3)} − RS ${fmt(calcul.rs, 3)})` : ''}
              </p>
              <button
                type="submit"
                data-testid="frais-submit"
                className="flex h-12 items-center justify-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
              >
                <Plus size={16} /> Enregistrer la facture
              </button>
            </div>
          </form>

          <section className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold text-slate-900">
              Factures de frais généraux <span className="font-data text-xs text-slate-500">({depenses.length})</span>
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left" data-testid="table-frais">
                <thead className="bg-slate-50">
                  <tr>
                    {['Réf.', 'Date', 'N° facture', 'Fournisseur', 'Catégorie', 'HT', 'Autres taxes', 'Base TVA', 'TVA', 'Timbre', 'TTC', 'RS', 'Net à payer', 'Réglé', 'Reste', 'Statut', 'Actions'].map((c) => (
                      <th key={c} className={th}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {depenses.length === 0 && (
                    <tr data-testid="frais-empty">
                      <td colSpan={17} className="px-3 py-10 text-center text-sm text-slate-500">Aucune facture de frais enregistrée.</td>
                    </tr>
                  )}
                  {depenses.map((d) => (
                    <tr key={d.id} data-testid={`frais-row-${d.id}`} className="hover:bg-slate-50">
                      <td className={`${td} font-data text-xs font-semibold`}>{d.numero}</td>
                      <td className={`${td} font-data text-xs`}>{d.date}</td>
                      <td className={`${td} font-data text-xs font-semibold`}>{d.numeroFacture}</td>
                      <td className={td}>{d.fournisseur}</td>
                      <td className={`${td} text-xs`}>{d.categorie}</td>
                      <td className={`${td} tabular font-data text-right`}>{fmt(d.montantHT, 3)}</td>
                      <td className={`${td} tabular font-data text-right text-xs`}>{fmt(d.autresTaxes ?? 0, 3)}</td>
                      <td className={`${td} tabular font-data text-right text-xs`}>{fmt(d.baseTVA ?? d.montantHT, 3)}</td>
                      <td className={`${td} tabular font-data text-right text-xs`}>{fmt(d.montantTVA, 3)}<span className="ml-1 text-slate-400">{d.tauxTVA}%</span></td>
                      <td className={`${td} tabular font-data text-right text-xs`}>{fmt(d.timbreFiscal ?? 0, 3)}</td>
                      <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(d.montantTTC, 3)}</td>
                      <td data-testid={`frais-rs-${d.id}`} className={`${td} tabular font-data text-right text-red-700`}>
                        {d.montantRS > 0 ? `${fmt(d.montantRS, 3)} (${String(d.tauxRS).replace('.', ',')}%)` : '—'}
                      </td>
                      <td data-testid={`frais-net-${d.id}`} className={`${td} tabular font-data text-right font-bold`}>
                        {fmt(d.netAPayer, 3)}
                        {d.avanceImputee > 0 && (
                          <span data-testid={`avance-imputee-${d.id}`} className="block font-data text-[10px] font-normal text-sky-700">
                            avance −{fmt(d.avanceImputee, 3)}
                          </span>
                        )}
                      </td>
                      <td className={`${td} tabular font-data text-right`}>{fmt(d.regle, 3)}</td>
                      <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(d.reste, 3)}</td>
                      <td className={td}>
                        <span data-testid={`frais-statut-${d.id}`} className={`border px-1.5 py-0.5 font-data text-[10px] font-bold uppercase tracking-[0.06em] ${COULEUR_STATUT[d.statut]}`}>
                          {d.statut}
                        </span>
                        {(d.reglements ?? []).map((r) => (
                          <span key={r.id} className="mt-1 block font-data text-[10px] text-slate-500">
                            {r.mode} {r.piece} · {fmt(r.montant, 3)} TND
                            {r.differe ? (r.decaisse ? ' · décaissé' : ` · échéance ${r.echeance}`) : ''}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          {d.reste > 0.001 && (
                            <button
                              type="button"
                              data-testid={`regler-${d.id}`}
                              onClick={() => setReglement({ depense: d, date: aujourdhui, mode: 'Espèces', piece: '', echeance: '', montant: d.reste, compteId: '' })}
                              className="border border-slate-900 bg-slate-900 px-2 py-1 text-xs font-bold text-white hover:bg-slate-700"
                            >
                              Régler
                            </button>
                          )}
                          {d.montantRS > 0 && (
                            <button type="button" data-testid={`certificat-${d.id}`} onClick={() => setCertificat(d)} title="Certificat de retenue à la source" className="border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-900 hover:text-white">
                              <Printer size={13} />
                            </button>
                          )}
                          {(d.reglements ?? []).length === 0 && (
                            <button type="button" data-testid={`supprimer-${d.id}`} onClick={() => supprimer(d)} className="border border-slate-300 p-1.5 text-slate-600 hover:border-red-600 hover:bg-red-600 hover:text-white">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {onglet === 'echeancier' && (
        <section data-testid="echeancier" className="border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="font-heading text-base font-bold text-slate-900">
              Échéancier de trésorerie <span className="font-data text-xs text-slate-500">({echeancier.length} effet(s) non décaissé(s))</span>
            </p>
            <p className="text-xs text-slate-500">
              Chèques et traites remis au fournisseur mais pas encore débités en banque. Le solde n'est
              impacté qu'au clic sur « Décaissé ».
            </p>
          </div>
          {enRetard.length > 0 && (
            <p data-testid="echeances-depassees" className="flex items-center gap-2 border-b-2 border-red-600 bg-red-50 px-4 py-2 text-xs font-bold text-red-800">
              <AlertTriangle size={14} /> {enRetard.length} effet(s) à échéance dépassée pour{' '}
              {fmt(enRetard.reduce((s, l) => s + l.montant, 0), 3)} TND — vérifiez le relevé bancaire.
            </p>
          )}
          <table className="w-full border-collapse text-left" data-testid="table-echeancier">
            <thead className="bg-slate-50">
              <tr>{['Échéance', 'Mode', 'N° pièce', 'Fournisseur', 'Facture', 'Compte à débiter', 'Montant (TND)', 'Remis le', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {echeancier.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500">Aucun paiement différé en attente de décaissement.</td></tr>
              )}
              {echeancier.map((l) => (
                <tr key={`${l.depenseId ?? ''}-${l.id}`} data-testid={`echeance-${l.id}`} className={l.echu ? 'bg-red-50' : ''}>
                  <td className={`${td} font-data text-xs font-bold ${l.echu ? 'text-red-700' : ''}`}>{l.echeance}</td>
                  <td className={td}>{l.mode}</td>
                  <td className={`${td} font-data text-xs`}>{l.piece}</td>
                  <td className={td}>{l.fournisseur}</td>
                  <td className={`${td} font-data text-xs`}>{l.numeroFacture}</td>
                  <td className={`${td} text-xs`}>{l.compte}</td>
                  <td className={`${td} tabular font-data text-right font-semibold`}>{fmt(l.montant, 3)}</td>
                  <td className={`${td} font-data text-xs text-slate-500`}>{l.date}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`decaisser-${l.id}`}
                      onClick={() => decaisser(l)}
                      className="flex h-9 items-center gap-1.5 border border-emerald-700 bg-emerald-700 px-2.5 text-xs font-bold text-white hover:bg-emerald-800"
                    >
                      <Check size={13} /> Décaissé
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100">
                <td className={`${th} text-left`} colSpan={6}>Total à décaisser (non échu)</td>
                <td className={`${th} text-right`}>{fmt(totalNonEchu, 3)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </section>
      )}

      {onglet === 'canonique' && (
        <div data-testid="frais-canonique" className="space-y-4">
          <DepensesCanoniques tiers={fournisseurs} />
          <FournisseursCanoniques tiers={fournisseurs} />
          <RetenuesCanoniques />
        </div>
      )}

      {onglet === 'tej' && (
        <section data-testid="declaration-tej" className="space-y-4">
          <div className="border border-slate-200 border-l-4 border-l-red-600 bg-white px-4 py-3">
            <p className="font-heading text-base font-bold text-slate-900">Dettes fiscales — Retenue à la source</p>
            <p className="tabular font-data text-2xl font-bold text-slate-900">{fmt(detteRS, 3)} TND</p>
            <p className="text-xs text-slate-500">
              Total des retenues opérées sur les fournisseurs, à reverser au Trésor et à télédéclarer sur la plateforme TEJ.
            </p>
          </div>
          {tej.length === 0 && (
            <p className="border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
              Aucune retenue à la source enregistrée.
            </p>
          )}
          {tej.map((m) => (
            <div key={m.mois} data-testid={`tej-mois-${m.mois}`} className="border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                <p className="font-heading text-sm font-bold text-slate-900">
                  {m.mois} — {m.nb} retenue(s) · base TTC {fmt(m.baseTTC, 3)} TND ·{' '}
                  <span className="text-red-700">RS {fmt(m.montantRS, 3)} TND</span>
                </p>
                <button
                  type="button"
                  data-testid={`export-tej-${m.mois}`}
                  onClick={() => exportTEJ(m)}
                  className="flex h-9 items-center gap-1.5 border border-slate-300 px-3 text-xs font-bold text-slate-800 hover:bg-slate-900 hover:text-white"
                >
                  <Download size={14} /> Export TEJ (CSV)
                </button>
              </div>
              <table className="w-full border-collapse text-left">
                <thead className="bg-slate-50">
                  <tr>{['Fournisseur', 'Matricule fiscal / CIN', 'Date de paiement', 'Nature de la retenue', 'Montant brut (base)', 'Taux', 'Montant RS', 'Montant net', ''].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {m.lignes.map((l) => (
                    <tr key={l.id} data-testid={`tej-ligne-${l.id}`}>
                      <td className={td}>{l.fournisseur}</td>
                      <td className={`${td} font-data text-xs`}>{l.mf || l.cin || '—'}</td>
                      <td className={`${td} font-data text-xs`}>{l.datePaiement ?? l.date}</td>
                      <td className={`${td} text-xs`}>{l.categorie}{l.libelle ? ` — ${l.libelle}` : ''}</td>
                      <td className={`${td} tabular font-data text-right`}>{fmt(l.montantTTC, 3)}</td>
                      <td className={`${td} font-data text-right text-xs`}>{String(l.tauxRS).replace('.', ',')} %</td>
                      <td className={`${td} tabular font-data text-right font-semibold text-red-700`}>{fmt(l.montantRS, 3)}</td>
                      <td className={`${td} tabular font-data text-right`}>{fmt(l.netAPayer, 3)}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" data-testid={`tej-certificat-${l.id}`} onClick={() => setCertificat({ ...l, fournisseur: l.fournisseur })} className="border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-900 hover:text-white">
                          <Printer size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      )}

      {reglement && (
        <div data-testid="reglement-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const { error } = await reglerDepense(reglement.depense.id, { ...reglement, utilisateur: user?.name });
              if (error) return toast.error(error);
              const differe = MODES_REGLEMENT.find((m) => m.id === reglement.mode)?.differe;
              toast.success(
                differe
                  ? `${reglement.mode} ${reglement.piece} enregistré — décaissement à l'échéance du ${reglement.echeance}`
                  : `Règlement de ${fmt(reglement.montant, 3)} TND décaissé`
              );
              setReglement(null);
              load();
            }}
            className="w-full max-w-lg border border-slate-200 bg-white"
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="font-heading text-lg font-black text-slate-900">Règlement du net à payer</h2>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                {reglement.depense.fournisseur} · facture {reglement.depense.numeroFacture} · reste {fmt(reglement.depense.reste, 3)} TND
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
              <div>
                <label className={label}>Date</label>
                <input data-testid="reglement-date" type="date" value={reglement.date} onChange={(e) => setReglement({ ...reglement, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Montant (TND)</label>
                <input data-testid="reglement-montant" type="number" step="0.001" min="0" value={reglement.montant} onChange={(e) => setReglement({ ...reglement, montant: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Mode de règlement</label>
                <select data-testid="reglement-mode" value={reglement.mode} onChange={(e) => setReglement({ ...reglement, mode: e.target.value })} className={inputCls}>
                  {MODES_REGLEMENT.map((m) => (
                    <option key={m.id} value={m.id}>{m.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Caisse / banque</label>
                <select data-testid="reglement-compte" value={reglement.compteId} onChange={(e) => setReglement({ ...reglement, compteId: e.target.value })} className={inputCls}>
                  <option value="">— Choisir —</option>
                  {comptes.map((c) => (
                    <option key={c.id} value={c.id}>{`${c.nom} — ${fmt(c.solde, 3)} TND`}</option>
                  ))}
                </select>
              </div>
              {MODES_REGLEMENT.find((m) => m.id === reglement.mode)?.differe ? (
                <>
                  <div>
                    <label className={label}>N° de pièce (obligatoire)</label>
                    <input data-testid="reglement-piece" value={reglement.piece} onChange={(e) => setReglement({ ...reglement, piece: e.target.value })} className={inputCls} placeholder="ex. CHQ-778120" />
                  </div>
                  <div>
                    <label className={label}>Date d'échéance (obligatoire)</label>
                    <input data-testid="reglement-echeance" type="date" value={reglement.echeance} onChange={(e) => setReglement({ ...reglement, echeance: e.target.value })} className={inputCls} />
                  </div>
                  <p data-testid="reglement-note-differe" className="sm:col-span-2 border-l-4 border-sky-600 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                    Paiement différé : la facture sera soldée face au fournisseur, mais le compte ne sera
                    débité qu'au clic sur « Décaissé » depuis l'échéancier de trésorerie.
                  </p>
                  <div className="sm:col-span-2 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      data-testid="copilote-traites"
                      onClick={() => setCopilote({ mode: 'ia', nombre: 3, enCours: false, propositions: [], commentaire: '' })}
                      className="flex h-11 flex-1 items-center justify-center gap-2 border-2 border-violet-700 bg-violet-50 px-3 font-heading text-sm font-bold text-violet-800 transition-colors duration-150 hover:bg-violet-700 hover:text-white"
                    >
                      <Sparkles size={16} /> Suggérer un échéancier (Copilote)
                    </button>
                    <button
                      type="button"
                      data-testid="echeancier-manuel"
                      onClick={() =>
                        setCopilote({
                          mode: 'manuel',
                          nombre: 0,
                          enCours: false,
                          commentaire: '',
                          propositions: [
                            {
                              echeance: reglement.echeance || reglement.date || aujourdhui,
                              montant: Number(reglement.depense.reste).toFixed(3),
                              piece: '',
                            },
                          ],
                        })
                      }
                      className="flex h-11 flex-1 items-center justify-center gap-2 border-2 border-slate-900 bg-white px-3 font-heading text-sm font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                    >
                      <Plus size={16} /> Saisir mon échéancier manuellement
                    </button>
                  </div>
                </>
              ) : (
                <div className="sm:col-span-2">
                  <label className={label}>N° de pièce (facultatif)</label>
                  <input data-testid="reglement-piece" value={reglement.piece} onChange={(e) => setReglement({ ...reglement, piece: e.target.value })} className={inputCls} />
                  <p className="mt-1.5 text-xs text-slate-500">Décaissement immédiat : le solde du compte est débité à la validation.</p>
                </div>
              )}
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button type="submit" data-testid="reglement-valider" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white hover:bg-slate-700">
                Enregistrer le règlement
              </button>
              <button type="button" data-testid="reglement-annuler" onClick={() => setReglement(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {copilote && reglement && (
        <div data-testid="copilote-dialog" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-2xl border-2 border-violet-700 bg-white">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="flex items-center gap-2 font-heading text-lg font-black text-slate-900">
                {copilote.mode === 'manuel' ? (
                  <>
                    <Plus size={18} className="text-slate-900" /> Échéancier manuel — {reglement.mode}
                  </>
                ) : (
                  <>
                    <Sparkles size={18} className="text-violet-700" /> Copilote — échéancier de traites
                  </>
                )}
              </h2>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                {reglement.depense.fournisseur} · net à payer {fmt(reglement.depense.reste, 3)} TND
              </p>
            </div>
            <div className="space-y-4 px-5 py-4">
              {copilote.mode === 'manuel' ? (
                <div className="flex flex-wrap items-center justify-between gap-3 border-l-4 border-slate-900 bg-slate-50 px-3 py-2">
                  <p className="text-xs text-slate-700">
                    Ajoutez autant d'échéances que nécessaire : dates et montants totalement libres, aucune contrainte de l'IA. Chaque ligne doit porter un n° de pièce (chèque / traite / virement) pour figurer à l'échéancier.
                  </p>
                  <button
                    type="button"
                    data-testid="manuel-ajouter-ligne"
                    onClick={() =>
                      setCopilote((c) => {
                        const place = c.propositions.reduce((s, p) => s + (Number(p.montant) || 0), 0);
                        const reste = Number((reglement.depense.reste - place).toFixed(3));
                        const derniere = c.propositions[c.propositions.length - 1]?.echeance || reglement.date || aujourdhui;
                        const suivante = new Date(derniere);
                        suivante.setMonth(suivante.getMonth() + 1);
                        return {
                          ...c,
                          propositions: [
                            ...c.propositions,
                            {
                              echeance: suivante.toISOString().slice(0, 10),
                              montant: reste > 0 ? reste.toFixed(3) : '',
                              piece: '',
                            },
                          ],
                        };
                      })
                    }
                    className="h-10 border-2 border-slate-900 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-slate-900 hover:bg-slate-900 hover:text-white"
                  >
                    + Ajouter une échéance
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className={label}>En combien de traites ?</label>
                  <input
                    data-testid="copilote-nombre"
                    type="number"
                    min="1"
                    max="24"
                    value={copilote.nombre}
                    onChange={(e) => setCopilote({ ...copilote, nombre: Number(e.target.value) })}
                    className={`${inputCls} w-28`}
                  />
                </div>
                <button
                  type="button"
                  data-testid="copilote-analyser"
                  disabled={copilote.enCours || !(copilote.nombre > 0)}
                  onClick={async () => {
                    setCopilote((c) => ({ ...c, enCours: true }));
                    const { error, propositions, commentaire } = await suggererTraites({
                      nombre: copilote.nombre,
                      montant: Number(reglement.depense.reste),
                      depart: reglement.date || aujourdhui,
                      beneficiaire: reglement.depense.fournisseur,
                    });
                    if (error) {
                      setCopilote((c) => ({ ...c, enCours: false }));
                      return toast.error(error);
                    }
                    setCopilote((c) => ({ ...c, enCours: false, propositions: propositions ?? [], commentaire: commentaire ?? '' }));
                  }}
                  className={`h-11 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white ${copilote.enCours ? 'cursor-wait bg-slate-400' : 'bg-violet-700 hover:bg-violet-800'}`}
                >
                  {copilote.enCours ? 'Analyse de l’échéancier…' : 'Analyser et proposer'}
                </button>
                </div>
              )}

              {copilote.commentaire && (
                <p data-testid="copilote-commentaire" className="border-l-4 border-violet-700 bg-violet-50 px-3 py-2 text-xs text-violet-900">
                  {copilote.commentaire}
                </p>
              )}

              {copilote.propositions.length > 0 && (
                <table className="w-full border-collapse text-left" data-testid="copilote-propositions">
                  <thead className="bg-slate-50">
                    <tr>
                      {(copilote.mode === 'manuel'
                        ? ['Échéance', 'Date d’échéance', 'Montant', 'N° pièce', '']
                        : ['Traite', 'Échéance proposée', 'Semaine', 'Charge de la semaine', 'Montant', 'N° pièce']
                      ).map((c, k) => <th key={`${c}-${k}`} className={th}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {copilote.propositions.map((p, i) => (
                      <tr key={i} data-testid={`proposition-${i}`}>
                        <td className={`${td} font-data text-xs`}>{i + 1}/{copilote.propositions.length}</td>
                        <td className="px-2 py-1">
                          <input
                            data-testid={`proposition-date-${i}`}
                            type="date"
                            value={p.echeance}
                            onChange={(e) => setCopilote((c) => ({ ...c, propositions: c.propositions.map((x, j) => (j === i ? { ...x, echeance: e.target.value } : x)) }))}
                            className="h-9 border border-slate-300 bg-white px-2 font-data text-xs outline-none focus:border-slate-900"
                          />
                        </td>
                        {copilote.mode !== 'manuel' && (
                          <>
                            <td className={`${td} font-data text-xs text-slate-500`}>{p.semaine}</td>
                            <td className={`${td} tabular font-data text-right text-xs ${p.chargeSemaine > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                              {fmt(p.chargeSemaine, 3)}{p.decale ? ' · décalée' : ''}
                            </td>
                          </>
                        )}
                        <td className="px-2 py-1">
                          <input
                            data-testid={`proposition-montant-${i}`}
                            type="number"
                            step="0.001"
                            value={p.montant}
                            onChange={(e) => setCopilote((c) => ({ ...c, propositions: c.propositions.map((x, j) => (j === i ? { ...x, montant: e.target.value } : x)) }))}
                            className="tabular h-9 w-28 border border-slate-300 bg-white px-2 text-right font-data text-xs outline-none focus:border-slate-900"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            data-testid={`proposition-piece-${i}`}
                            value={p.piece ?? `${reglement.mode === 'Chèque' ? 'CHQ' : 'TRT'}-${i + 1}`}
                            onChange={(e) => setCopilote((c) => ({ ...c, propositions: c.propositions.map((x, j) => (j === i ? { ...x, piece: e.target.value } : x)) }))}
                            className="h-9 w-28 border border-slate-300 bg-white px-2 font-data text-xs outline-none focus:border-slate-900"
                          />
                        </td>
                        {copilote.mode === 'manuel' && (
                          <td className="px-2 py-1 text-right">
                            <button
                              type="button"
                              data-testid={`manuel-retirer-${i}`}
                              disabled={copilote.propositions.length === 1}
                              onClick={() => setCopilote((c) => ({ ...c, propositions: c.propositions.filter((_, j) => j !== i) }))}
                              className="border border-slate-300 p-1.5 text-slate-500 hover:bg-red-600 hover:text-white disabled:opacity-40"
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100">
                      <td className={`${th} text-left`} colSpan={copilote.mode === 'manuel' ? 2 : 4}>
                        {copilote.mode === 'manuel' ? 'Total saisi' : 'Total proposé'}
                      </td>
                      <td className={`${th} text-right`} colSpan={copilote.mode === 'manuel' ? 3 : 2}>
                        {fmt(copilote.propositions.reduce((s, p) => s + (Number(p.montant) || 0), 0), 3)} / {fmt(reglement.depense.reste, 3)} TND
                        {copilote.mode === 'manuel' && (
                          <span data-testid="manuel-ecart" className="ml-2 font-normal normal-case text-slate-600">
                            {(() => {
                              const ecart = Number((reglement.depense.reste - copilote.propositions.reduce((s, p) => s + (Number(p.montant) || 0), 0)).toFixed(3));
                              return Math.abs(ecart) <= 0.001
                                ? '· réparti au centime'
                                : ecart > 0
                                  ? `· ${fmt(ecart, 3)} TND resteront dus`
                                  : `· dépassement de ${fmt(-ecart, 3)} TND`;
                            })()}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                data-testid="copilote-valider"
                disabled={copilote.propositions.length === 0}
                onClick={async () => {
                  const total = copilote.propositions.reduce((s, p) => s + (Number(p.montant) || 0), 0);
                  const manuel = copilote.mode === 'manuel';
                  // Mode manuel : répartition totalement libre, seul un dépassement du reste dû est refusé
                  // (contrainte comptable, pas une contrainte de l'IA). Le Copilote reste strict.
                  if (!manuel && Math.abs(total - reglement.depense.reste) > 0.01)
                    return toast.error(`Le total des traites (${total.toFixed(3)}) doit égaler le net à payer (${reglement.depense.reste.toFixed(3)} TND).`);
                  if (manuel) {
                    if (total <= 0) return toast.error('Saisissez au moins un montant d’échéance.');
                    if (copilote.propositions.some((p) => !p.echeance)) return toast.error('Chaque échéance doit avoir une date.');
                    if (copilote.propositions.some((p) => !String(p.piece ?? '').trim()))
                      return toast.error('Chaque échéance doit porter un n° de pièce (chèque, traite ou virement).');
                    if (total - reglement.depense.reste > 0.01)
                      return toast.error(`Le total saisi (${total.toFixed(3)}) dépasse le reste à payer (${reglement.depense.reste.toFixed(3)} TND).`);
                  }
                  for (const [i, p] of copilote.propositions.entries()) {
                    const { error } = await reglerDepense(reglement.depense.id, {
                      date: reglement.date,
                      mode: reglement.mode,
                      piece: p.piece ?? `${reglement.mode === 'Chèque' ? 'CHQ' : 'TRT'}-${i + 1}`,
                      echeance: p.echeance,
                      montant: Number(p.montant),
                      compteId: reglement.compteId,
                      utilisateur: user?.name,
                    });
                    if (error) return toast.error(`Traite ${i + 1} : ${error}`);
                  }
                  toast.success(
                    copilote.mode === 'manuel'
                      ? `${copilote.propositions.length} échéance(s) enregistrée(s) — ${fmt(total, 3)} TND inscrits à l'échéancier${
                          reglement.depense.reste - total > 0.01 ? ` · reste dû ${fmt(reglement.depense.reste - total, 3)} TND` : ''
                        }`
                      : `${copilote.propositions.length} traites enregistrées et inscrites à l'échéancier`
                  );
                  setCopilote(null);
                  setReglement(null);
                  load();
                }}
                className={`h-11 flex-1 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white ${copilote.propositions.length === 0 ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-700 hover:bg-emerald-800'}`}
              >
                Valider l'échéancier {copilote.mode === 'manuel' ? 'saisi' : 'proposé'}
              </button>
              <button type="button" data-testid="copilote-fermer" onClick={() => setCopilote(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {avance && (
        <div data-testid="avance-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <form
            onSubmit={async (ev) => {
              ev.preventDefault();
              const { error, row } = await verserAvance({ ...avance, utilisateur: user?.name });
              if (error) return toast.error(error);
              toast.success(`Avance de ${fmt(avance.montant, 3)} TND versée — solde créditeur disponible`);
              setRecuAvance({
                ...row,
                fournisseur: fournisseurs.find((c) => c.id === row.tiersId)?.nom ?? '—',
                compteNom: comptes.find((c) => c.id === row.compteId)?.nom ?? '',
                impute: 0,
                reste: soldeAvance(row.tiersId, avances) + row.montant,
              });
              setAvance(null);
              load();
            }}
            className="w-full max-w-lg border border-slate-200 bg-white"
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="font-heading text-lg font-black text-slate-900">Verser une avance fournisseur</h2>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                Décaissement immédiat, imputé automatiquement sur la prochaine facture
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={label}>Fournisseur général *</label>
                <ContactCombobox
                  contacts={fournisseurs}
                  value={avance.tiersId}
                  onChange={(id) => setAvance({ ...avance, tiersId: id })}
                  testid="avance-fournisseur"
                  placeholder="Rechercher un fournisseur général…"
                />
                {avance.tiersId && (
                  <p className="mt-1.5 font-data text-[11px] text-sky-800">
                    Solde créditeur actuel : {fmt(soldeAvance(avance.tiersId, avances), 3)} TND
                  </p>
                )}
              </div>
              <div>
                <label className={label}>Date</label>
                <input data-testid="avance-date" type="date" value={avance.date} onChange={(ev) => setAvance({ ...avance, date: ev.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Montant (TND) *</label>
                <input data-testid="avance-montant" type="number" step="0.001" min="0" value={avance.montant} onChange={(ev) => setAvance({ ...avance, montant: ev.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={label}>Mode de paiement</label>
                <select data-testid="avance-mode" value={avance.mode} onChange={(ev) => setAvance({ ...avance, mode: ev.target.value })} className={inputCls}>
                  {MODES_REGLEMENT.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>N° de pièce</label>
                <input data-testid="avance-piece" value={avance.piece} onChange={(ev) => setAvance({ ...avance, piece: ev.target.value })} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Caisse / banque à débiter *</label>
                <select data-testid="avance-compte" value={avance.compteId} onChange={(ev) => setAvance({ ...avance, compteId: ev.target.value })} className={inputCls}>
                  <option value="">— Choisir —</option>
                  {comptes.map((c) => <option key={c.id} value={c.id}>{`${c.nom} — ${fmt(c.solde, 3)} TND`}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button type="submit" data-testid="avance-valider" className="h-11 flex-1 bg-sky-700 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white hover:bg-sky-800">
                Enregistrer l'avance
              </button>
              <button type="button" data-testid="avance-annuler" onClick={() => setAvance(null)} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {recuAvance && <RecuAvance avance={recuAvance} onClose={() => setRecuAvance(null)} />}

      {certificat && (
        <CertificatRS
          depense={certificat}
          fournisseur={fournisseurs.find((c) => c.id === certificat.tiersId)}          onClose={() => setCertificat(null)}
        />
      )}
    </div>
  );
}
